import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Building, MapPin, Phone, Mail, FileText,
  Package, TrendingUp, CheckCircle, Eye, ChevronLeft, ChevronRight, Loader2,
  ShieldCheck, ShieldAlert, Download, AlertTriangle, Share2, Copy, Check, RefreshCw, Send, MessageCircle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const STATUS_COLORS = {
  PENDING:    'bg-yellow-100 text-yellow-700 border-yellow-200',
  BOOKED:     'bg-blue-100 text-blue-700 border-blue-200',
  IN_TRANSIT: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  ARRIVED:    'bg-purple-100 text-purple-700 border-purple-200',
  DELIVERED:  'bg-green-100 text-green-700 border-green-200',
  COMPLETED:  'bg-green-100 text-green-800 border-green-200',
  CANCELLED:  'bg-red-100 text-red-700 border-red-200',
};

const TYPE_COLORS = {
  AIR:     'bg-sky-100 text-sky-700',
  SEA:     'bg-blue-100 text-blue-700',
  ROAD:    'bg-orange-100 text-orange-700',
  RAIL:    'bg-yellow-100 text-yellow-700',
  COURIER: 'bg-purple-100 text-purple-700',
};

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Share & Re-KYC modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [rekycModalOpen, setRekycModalOpen] = useState(false);
  const [rekycRemarks, setRekycRemarks] = useState('');
  const [rekycSubmitting, setRekycSubmitting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Docket list state
  const [dockets, setDockets] = useState([]);
  const [docketPage, setDocketPage] = useState(1);
  const [docketTotal, setDocketTotal] = useState(0);
  const [docketTotalPages, setDocketTotalPages] = useState(1);
  const [docketLoading, setDocketLoading] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');  // '' = All branches
  const DOCKET_LIMIT = 15;

  useEffect(() => { fetchProfile(); }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!id) return;
    let active = true;
    const branchParam = branchFilter ? `&branch_id=${branchFilter}` : '';
    const load = async () => {
      if (active) setDocketLoading(true);
      const res = await fetch(
        `${API}/api/customers/${id}/dockets?page=${docketPage}&limit=${DOCKET_LIMIT}${branchParam}`,
        { headers: authHeaders() }
      ).catch(() => null);
      if (!active || !res) { if (active) setDocketLoading(false); return; }
      const data = await res.json().catch(() => ({}));
      if (!active) return;
      setDockets(data.items || []);
      setDocketTotal(data.total || 0);
      setDocketTotalPages(data.total_pages || 1);
      setDocketLoading(false);
    };
    load();
    return () => { active = false; };
  }, [id, docketPage, branchFilter]);

  const handleBranchFilter = (val) => {
    setBranchFilter(val);
    setDocketPage(1);  // reset to page 1 on filter change
  };

  const getPublicKycUrl = (kycToken) => {
    const base = window.location.origin;
    return `${base}/kyc-onboarding/${kycToken || ''}`;
  };

  const handleShareWhatsApp = (token, businessName, phone) => {
    const url = getPublicKycUrl(token);
    const text = `Dear *${businessName}*,\n\nPlease complete your company KYC onboarding and upload required documents (IEC, PAN Card, GST Copy, CHA Authority Letter, KYC Form, Aadhar Card) using this secure link:\n${url}\n\nThank you,\n*Stavan Logistics Compliance Team*`;
    const cleanPhone = phone ? phone.replace(/[^0-9]/g, '') : '';
    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(text)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  const handleShareEmail = (token, businessName, email) => {
    const url = getPublicKycUrl(token);
    const subject = `Company KYC Onboarding Request - ${businessName}`;
    const body = `Dear ${businessName},\n\nPlease complete your company KYC onboarding and upload required compliance documents (IEC, PAN Card, GST Copy, CHA Authority Letter, KYC Form, Aadhar Card) using this secure link:\n\n${url}\n\nNote: All uploaded documents will be securely routed to our compliance desk for verification.\n\nBest Regards,\nStavan Logistics Team`;
    window.open(`mailto:${email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const handleCopyLink = (token) => {
    const url = getPublicKycUrl(token);
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
    toast({ title: 'Copied', description: 'Public KYC link copied to clipboard.' });
  };

  const handleRequestRekyc = async () => {
    try {
      setRekycSubmitting(true);
      const res = await fetch(`${API}/api/customers/${id}/rekyc`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ remarks: rekycRemarks || 'Admin requested updated KYC documents' })
      });
      if (!res.ok) throw new Error('Failed to request Re-KYC');
      toast({ title: 'Re-KYC Requested', description: 'Customer KYC is unlocked for document updates.' });
      setRekycModalOpen(false);
      fetchProfile();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setRekycSubmitting(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/api/customers/${id}/profile`, { headers: authHeaders() });
      const data = await res.json();
      setProfileData(data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-accent-blue animate-spin" />
      </div>
    );
  }

  if (!profileData) return null;

  const { customer, statistics } = profileData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/customers')} className="h-8 w-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-text-dark font-manrope">
                {customer.business_name}
              </h1>
              <div className="flex flex-wrap gap-1">
                {(customer.business_roles || ['SHIPPER']).map((r, i) => {
                  const rKey = String(r).toUpperCase();
                  return (
                    <span
                      key={i}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded tracking-wider uppercase ${
                        rKey === 'AGENT'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : rKey === 'FORWARDER'
                          ? 'bg-purple-100 text-purple-800 border border-purple-200'
                          : rKey === 'CONSIGNEE'
                          ? 'bg-sky-100 text-sky-800 border border-sky-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {rKey === 'SHIPPER' ? 'Shipper' : rKey === 'CONSIGNEE' ? 'Consignee / Importer' : rKey === 'FORWARDER' ? 'Forwarder' : 'Agent'}
                    </span>
                  );
                })}
              </div>
            </div>
            <p className="text-sm text-text-secondary mt-1">Customer Profile</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/customers/edit/${id}`)}>
          Edit Customer
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider">Total Dockets</p>
              <p className="text-2xl font-bold text-text-dark mt-1 font-manrope" data-testid="stat-total-dockets">
                {statistics.total_dockets}
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent-blue" />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider">Active Dockets</p>
              <p className="text-2xl font-bold text-text-dark mt-1 font-manrope" data-testid="stat-active-dockets">
                {statistics.active_shipments}
              </p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider">Completed Dockets</p>
              <p className="text-2xl font-bold text-text-dark mt-1 font-manrope" data-testid="stat-completed-dockets">
                {statistics.completed_deliveries}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider">Branches</p>
              <p className="text-2xl font-bold text-text-dark mt-1 font-manrope" data-testid="stat-branches">
                {statistics.total_branches}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Building className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Business Details + Branches side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Details */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark font-manrope mb-5 flex items-center gap-2">
            <Building className="w-5 h-5 text-accent-blue" />
            Business Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">Business Name</p>
              <p className="text-base text-text-dark font-semibold">{customer.business_name}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">PAN Number</p>
              <p className="text-base text-text-dark font-ibm">{customer.pan_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">Status</p>
              <span className={`inline-block text-xs font-semibold px-2 py-1 rounded-full font-ibm
                ${customer.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {customer.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">Business Roles</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {(customer.business_roles || ['SHIPPER']).map((r, i) => {
                  const rKey = String(r).toUpperCase();
                  return (
                    <span
                      key={i}
                      className={`text-xs font-semibold px-2 py-0.5 rounded font-ibm ${
                        rKey === 'AGENT'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : rKey === 'FORWARDER'
                          ? 'bg-purple-100 text-purple-800 border border-purple-200'
                          : rKey === 'CONSIGNEE'
                          ? 'bg-sky-100 text-sky-800 border border-sky-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {rKey === 'SHIPPER' ? 'Shipper / Exporter' : rKey === 'CONSIGNEE' ? 'Consignee / Importer' : rKey === 'FORWARDER' ? 'Forwarder' : 'Agent'}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* Branch Locations */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark font-manrope mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-accent-blue" />
            Branch Locations ({customer.branches?.length || 0})
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {customer.branches?.map((branch) => (
              <div key={branch.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-text-dark text-sm">{branch.branch_nick_name}</p>
                  {branch.is_head_office && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-ibm">Head Office</span>
                  )}
                </div>
                {branch.contact_person && (
                  <p className="text-xs text-text-secondary font-ibm mb-1.5">{branch.contact_person}</p>
                )}
                <div className="space-y-0.5">
                  {/* Full Address */}
                  {(branch.address_line_1 || branch.city) && (
                    <div className="flex items-start gap-1.5 text-xs text-text-secondary">
                      <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                      <span>
                        {[branch.address_line_1, branch.address_line_2, branch.city, branch.state, branch.pincode, branch.country]
                          .filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {/* GST Number */}
                  {branch.gst_number && (
                    <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <span className="font-semibold text-text-dark">GST:</span>
                      <span className="font-mono">{branch.gst_number}</span>
                    </div>
                  )}
                  {branch.contact_numbers?.filter(Boolean).length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <Phone className="w-3 h-3 shrink-0" />
                      <span>{branch.contact_numbers.filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {branch.emails?.filter(Boolean).length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span>{branch.emails.filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* KYC Documents Section */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-dark font-manrope flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-accent-green" />
              KYC Documents & Verification
            </h3>
            <p className="text-xs text-text-secondary font-ibm mt-0.5">
              Required: COI, MOA, AOA, POA, PAN Allotment, IEC, PAN Card, GST, CHA Authority, KYC Form, Aadhar, Telephone Bill
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {customer.rekyc_requested ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-600" />
                Re-KYC Active
              </span>
            ) : customer.kyc_status === 'VERIFIED' ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                KYC Verified ({customer.kyc_completed_count || 0}/{customer.kyc_required_count || 6})
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                KYC Pending ({customer.kyc_completed_count || 0}/{customer.kyc_required_count || 6})
              </span>
            )}

            {/* Share KYC Link Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareModalOpen(true)}
              className="h-8 text-xs font-ibm bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              title="Share KYC onboarding link with client via WhatsApp or Email"
            >
              <Share2 className="w-3.5 h-3.5 mr-1 text-blue-600" />
              Share KYC Form
            </Button>

            {/* Request Re-KYC Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRekycRemarks(customer.rekyc_remarks || '');
                setRekycModalOpen(true);
              }}
              className="h-8 text-xs font-ibm bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
              title="Unlock customer documents and request fresh KYC upload"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1 text-amber-600" />
              Request Re-KYC
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/customers/edit/${customer.id}#kyc`)}
              className="h-8 text-xs font-ibm text-accent-blue hover:underline"
            >
              Manage KYC →
            </Button>
          </div>
        </div>

        {customer.missing_kyc_docs?.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <span className="font-semibold text-amber-900">Missing Required Documents:</span>{' '}
            {customer.missing_kyc_docs.join(', ')}
          </div>
        )}

        {customer.kyc_documents?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {customer.kyc_documents.map((doc) => (
              <div key={doc.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-text-dark font-ibm">{doc.document_name}</p>
                  <p className="text-xs text-text-secondary truncate max-w-[180px]">{doc.file_name}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const token = localStorage.getItem('auth_token');
                    window.open(`${API}/api/drive/files/${encodeURIComponent(doc.drive_file_id)}/download?token=${token}`, '_blank');
                  }}
                  className="h-8 text-xs font-ibm text-accent-blue"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  View
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-secondary italic">No documents uploaded yet.</p>
        )}
      </Card>

      {/* All Dockets — paginated */}
      <Card className="p-6" data-testid="customer-dockets-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-dark font-manrope flex items-center gap-2">
            <Package className="w-5 h-5 text-accent-blue" />
            All Dockets
            <span className="text-sm font-normal text-text-secondary font-ibm ml-1">
              ({docketTotal} {branchFilter ? 'filtered' : 'lifetime'})
            </span>
          </h3>

          {/* Branch filter */}
          {profileData?.customer?.branches?.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-text-secondary font-ibm">Branch:</label>
              <select
                data-testid="branch-filter-select"
                value={branchFilter}
                onChange={e => handleBranchFilter(e.target.value)}
                className="text-sm font-ibm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-text-dark focus:outline-none focus:ring-2 focus:ring-accent-blue cursor-pointer"
              >
                <option value="">All Branches</option>
                {profileData.customer.branches.map(b => (
                  <option key={b.id} value={b.id}>{b.branch_nick_name}</option>
                ))}
              </select>
              {branchFilter && (
                <button
                  onClick={() => handleBranchFilter('')}
                  className="text-xs text-accent-blue hover:underline font-ibm"
                  data-testid="clear-branch-filter"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {docketLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-accent-blue animate-spin" />
          </div>
        ) : dockets.length === 0 ? (
          <div className="text-center py-10">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-text-secondary font-ibm">No dockets found for this customer</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Docket No</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Branch</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Type</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Date</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {dockets.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-3 font-semibold text-text-dark font-ibm">{d.system_docket_no}</td>
                      <td className="py-3 px-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-semibold text-text-dark font-ibm">{d.branch_name || '—'}</span>
                          {d.roles?.length > 0 && (
                            <div className="flex gap-1 flex-wrap">
                              {d.roles.map(role => (
                                <span key={role} className={`text-xs px-1.5 py-0.5 rounded font-ibm font-medium
                                  ${role === 'Exporter' ? 'bg-blue-50 text-blue-600' :
                                    role === 'Importer' ? 'bg-purple-50 text-purple-600' :
                                    'bg-orange-50 text-orange-600'}`}>
                                  {role}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded font-ibm ${TYPE_COLORS[d.shipment_type] || 'bg-gray-100 text-gray-600'}`}>
                          {d.shipment_type}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border font-ibm ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-text-secondary font-ibm text-xs">{formatDate(d.created_at)}</td>
                      <td className="py-3 px-3 text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => navigate(`/dockets/view/${d.id}`)}
                          className="h-7 w-7 p-0 hover:bg-blue-50"
                          data-testid={`view-docket-${d.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 text-accent-blue" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {docketTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-text-secondary font-ibm">
                  Showing {((docketPage - 1) * DOCKET_LIMIT) + 1}–{Math.min(docketPage * DOCKET_LIMIT, docketTotal)} of {docketTotal} dockets
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm"
                    disabled={docketPage <= 1}
                    onClick={() => setDocketPage(p => p - 1)}
                    className="h-8 w-8 p-0"
                    data-testid="docket-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: Math.min(docketTotalPages, 5) }, (_, i) => {
                    const pg = docketTotalPages <= 5 ? i + 1
                      : docketPage <= 3 ? i + 1
                      : docketPage >= docketTotalPages - 2 ? docketTotalPages - 4 + i
                      : docketPage - 2 + i;
                    return (
                      <Button
                        key={pg} variant={docketPage === pg ? 'default' : 'ghost'} size="sm"
                        onClick={() => setDocketPage(pg)}
                        className={`h-8 w-8 p-0 text-xs font-ibm ${docketPage === pg ? 'bg-accent-blue text-white' : ''}`}
                        data-testid={`docket-page-${pg}`}
                      >
                        {pg}
                      </Button>
                    );
                  })}
                  <Button
                    variant="ghost" size="sm"
                    disabled={docketPage >= docketTotalPages}
                    onClick={() => setDocketPage(p => p + 1)}
                    className="h-8 w-8 p-0"
                    data-testid="docket-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Share KYC Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-6 font-ibm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 font-manrope flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Share KYC Form with Client
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">
              Share this secure onboarding link with <span className="font-semibold text-slate-800">{customer?.business_name}</span>. The client can open it without logging in to fill branch details and upload KYC documents. The company name is fixed and cannot be changed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Public Onboarding Link</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  readOnly
                  value={getPublicKycUrl(customer?.kyc_token)}
                  className="text-xs font-mono bg-slate-50 border-slate-200 h-9"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyLink(customer?.kyc_token)}
                  className="h-9 px-3 text-xs shrink-0"
                >
                  {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-600" />}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              {/* WhatsApp Share */}
              <Button
                type="button"
                onClick={() => {
                  const hoBranch = customer?.branches?.[0];
                  const phone = hoBranch?.contact_numbers?.[0];
                  handleShareWhatsApp(customer?.kyc_token, customer?.business_name, phone);
                }}
                className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs flex items-center justify-center gap-2 rounded-xl"
              >
                <MessageCircle className="w-4 h-4" />
                Send via WhatsApp
              </Button>

              {/* Email Share */}
              <Button
                type="button"
                onClick={() => {
                  const hoBranch = customer?.branches?.[0];
                  const email = hoBranch?.emails?.[0];
                  handleShareEmail(customer?.kyc_token, customer?.business_name, email);
                }}
                className="h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs flex items-center justify-center gap-2 rounded-xl"
              >
                <Mail className="w-4 h-4" />
                Send via Email
              </Button>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShareModalOpen(false)}
              className="text-xs"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Re-KYC Modal */}
      <Dialog open={rekycModalOpen} onOpenChange={setRekycModalOpen}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-6 font-ibm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 font-manrope flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-600" />
              Request Re-KYC from Customer
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">
              Triggering Re-KYC unlocks the client's KYC submission and notifies them to upload updated copies of statutory documents.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 my-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Reason / Remarks for Re-KYC</Label>
              <textarea
                rows={3}
                value={rekycRemarks}
                onChange={(e) => setRekycRemarks(e.target.value)}
                placeholder="e.g. Expired IEC certificate, or updated GST certificate required for new branch location."
                className="w-full mt-1.5 p-2.5 text-xs font-ibm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRekycModalOpen(false)}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRequestRekyc}
              disabled={rekycSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white font-medium text-xs h-9 px-4 rounded-lg"
            >
              {rekycSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Confirm & Request Re-KYC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
